import { makeStyles } from '@material-ui/styles';
import cn from 'clsx';

import { LocationType } from '../../types';
import useTypeTracking from '../../../../analytics';

import { Textarea, Text, VidiconLine, CupLine, Theme, MainButton } from '@bizzabo/design-system';
import useIsMobile from '../../../../hooks/useIsMobile';

interface FirstPagePropsType {
    introMessage: string;
    setIntroMessage: (introMessage: string) => void;
    locationType: LocationType;
    setLocationType: (locationType: LocationType) => void;
    physicalLocationText: string;
    setPhysicalLocationText: (physicalLocationText: string) => void;
    nameToDisplay: string;
}

const MIN_ROWS_INTRO = 3;
const MIN_ROWS_LOCATION = 1;
const MAX_ROWS_INTRO_DESKTOP = 9;
const MAX_ROWS_LOCATION_DESKTOP = 4;
const MAX_ROWS_INTRO_MOBILE = 5;
const MAX_ROWS_LOCATION_MOBILE = 3;

const FirstPage: React.FC<FirstPagePropsType> = ({
    introMessage,
    setIntroMessage,
    locationType,
    setLocationType,
    physicalLocationText,
    setPhysicalLocationText,
    nameToDisplay,
}) => {
    const isMobile = useIsMobile();
    const maxRowsIntro = isMobile ? MAX_ROWS_INTRO_MOBILE : MAX_ROWS_INTRO_DESKTOP;
    const maxRowsLocation = isMobile ? MAX_ROWS_LOCATION_MOBILE : MAX_ROWS_LOCATION_DESKTOP;

    const { trackClickEvent } = useTypeTracking();

    const onMessageFocus = () => {
        trackClickEvent({
            subCategory: 'MEETING_INVITATION_TEXT_EDIT',
        });
    };

    const onLocationFocus = () => {
        trackClickEvent({
            subCategory: 'LOCATION_DETAILS_EDITED',
        });
    };

    const introChangeHandle = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setIntroMessage(event.target.value);
    };

    const locationChangeHandle = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPhysicalLocationText(event.target.value);
    };

    const classes = useStyles();
    return (
        <div className={classes.wrapper}>
            <div>
                <Text variant="t50">
                    Send <span className={classes.bold}>{nameToDisplay}</span> a meeting request
                </Text>
            </div>
            <div className={classes.formSection}>
                <Text variant="t81" className={cn(classes.label, classes.introLabel)}>
                    Customize the intro message and pick the location. Next, preview your
                    availability before sending it to {nameToDisplay}.
                </Text>
                <Textarea
                    className={cn(classes.textArea, classes.introTextarea)}
                    labelClassName={classes.textAreaLabel}
                    value={introMessage}
                    onChange={introChangeHandle}
                    onFocus={onMessageFocus}
                    label={'Customize your intro message'}
                    minRows={MIN_ROWS_INTRO}
                    maxRows={maxRowsIntro}
                    maxLength={300}
                />
            </div>
            <div>
                <Text variant="t81" className={cn(classes.inputLabel, classes.label)}>
                    How will you meet?
                </Text>
            </div>
            <div className={cn(classes.locationButtons, classes.formSection)}>
                <div className={classes.toggleButtons}>
                    <MainButton
                        className={cn(classes.firstLocationButton, classes.locationButton)}
                        variant={locationType === LocationType.Physical ? 'fill' : 'stroke'}
                        before={<CupLine />}
                        onClick={() => setLocationType(LocationType.Physical)}
                    >
                        In-person
                    </MainButton>
                    <MainButton
                        className={classes.locationButton}
                        variant={locationType === LocationType.Virtual ? 'fill' : 'stroke'}
                        before={<VidiconLine />}
                        onClick={() => setLocationType(LocationType.Virtual)}
                    >
                        Virtual
                    </MainButton>
                </div>
            </div>
            {locationType === LocationType.Physical && (
                <div className={classes.formSection}>
                    <Textarea
                        className={cn(classes.textArea, classes.locationTextarea)}
                        labelClassName={classes.textAreaLabel}
                        value={physicalLocationText}
                        onFocus={onLocationFocus}
                        onChange={locationChangeHandle}
                        label="Where will the meeting take place?"
                        placeholder="Enter location details"
                        minRows={MIN_ROWS_LOCATION}
                        maxRows={maxRowsLocation}
                        maxLength={50}
                    />
                </div>
            )}
        </div>
    );
};

const useStyles = makeStyles<Theme>(
    theme => ({
        wrapper: {
            overflowY: 'auto',
            marginBottom: 16,
        },
        bold: {
            fontWeight: 'bold',
        },
        label: {
            color: theme.customization.background.G10,
            lineHeight: '18px',
        },
        inputLabel: {
            paddingLeft: 8,
        },
        introLabel: {
            marginBottom: 16,
        },
        textArea: {
            backgroundColor: theme.customization.background.C10,
            color: theme.customization.background.G10,
            '&::placeholder': {
                color: theme.customization.background.G30,
            },
        },
        introTextarea: {
            minHeight: 80,
        },
        locationTextarea: {
            minHeight: 32,
        },
        formSection: {
            marginBottom: 24,
            paddingLeft: 1,
            paddingRight: 1,
        },
        textAreaLabel: {
            color: theme.customization.background.G10,
        },
        locationButtons: {
            display: 'flex',
            flexDirection: 'row',
        },
        toggleButtons: {
            display: 'flex',
            flexDirection: 'row',
            backgroundColor: theme.customization.background.C10,
            borderColor: theme.customization.background.C10,
            padding: 6,
            borderRadius: 25,
        },
        locationButton: {
            ...theme.typography.t71,
            padding: '4px 25px',
            border: `1px solid ${theme.customization.background.G10}`,
        },
        firstLocationButton: {
            marginRight: 8,
        },
    }),
    { classNamePrefix: 'meetings-scheduling-first-page' }
);

export default FirstPage;
